// @flow
import React from 'react';
import { connect } from 'react-redux';
import changeCarouselSlotIndex from 'app/common/redux/actions/pin/carousel';
import getCarouselData from 'app/common/react/components/common/Carousel/utils/getCarouselData';
import type { CarouselPinData } from 'app/packages/pinterest-flow-types/pin';
import type { Pin } from 'app/common/react/components/closeup/types';
import { getCommerceAuxData } from 'app/common/lib/CommerceUtil';
import { SingleTreatmentExp } from 'app/common/lib/experiment';
import { track } from 'app/common/lib/react/analytics';
import { type CloseupViewType } from 'app/common/react/components/closeup/types';
import { type State as ReduxState } from 'app/common/redux/reducers/types';
import {
  withRequestContext,
  type RequestContextType,
} from 'app/common/react/components/RequestContext';
import { Box, Touchable } from 'gestalt';
import { compose, type Dispatch } from 'redux';

const NOOP = () => {};

type ReduxStateProps = {|
  carouselData?: ?CarouselPinData,
  pin?: Pin,
|};

type ReduxDispatchProps = {|
  changeCarouselSlotIndex: (pinId: string, newIndex: number) => void,
|};

type HOCProps = {|
  requestContext: RequestContextType,
|};

type OwnProps = {|
  carouselData?: ?CarouselPinData,
  carouselIndex?: number,
  closeupImageHovered?: boolean,
  componentType?: number,
  contextLogData?: Object,
  handleCarouselSwipe?: (index: number) => void,
  isCloseup?: boolean,
  isEditMode?: boolean,
  pinId: string,
  viewParameter?: number,
  viewType?: number,
  variant?: CloseupViewType,
|};

type ConnectedProps = {|
  ...OwnProps,
  ...ReduxStateProps,
  ...ReduxDispatchProps,
|};

type Props = {|
  ...ReduxDispatchProps,
  ...HOCProps,
  ...OwnProps,
  ...ReduxStateProps,
|};

const logSwipe = (
  pin: Pin,
  pinId: string,
  currentIndex: number,
  nextIndex: number,
  carouselData: CarouselPinData,
  viewParameter?: number,
  viewType?: number,
  componentType?: number,
  contextLogData?: Object,
  isEditMode?: boolean,
) => {
  if (!isEditMode) {
    const { carousel_slots, id: carouselId } = carouselData;
    track(PCONST.thrift.EventType.SWIPE, {
      ...contextLogData,
      objectId: pinId,
      component: componentType,
      view: viewType,
      viewParameter,
      eventData: {
        pinCarouselSlotEventData: {
          carouselSlotId: carousel_slots[currentIndex] && Number(carousel_slots[currentIndex].id),
          carouselDataId: Number(carouselId),
          carouselSlotIndex: currentIndex,
          toCarouselSlotIndex: nextIndex,
        },
      },
      ...(getCommerceAuxData(pin) || {}),
    });
  }
};

type State = {|
  isHovered: boolean,
|};

export class CarouselEllipsis extends React.Component<Props, State> {
  state = {
    isHovered: false,
  };

  handleMouseEnter = () => {
    this.setState({ isHovered: true });
  };

  handleMouseLeave = () => {
    this.setState({ isHovered: false });
  };

  render() {
    const {
      carouselIndex,
      carouselData,
      changeCarouselSlotIndex,
      handleCarouselSwipe,
      isCloseup,
      pin,
      pinId,
      contextLogData,
      requestContext,
      viewParameter,
      viewType,
      componentType,
      isEditMode,
      closeupImageHovered = false,
      variant,
    } = this.props;

    if (!carouselData) {
      return null;
    }

    const { isHovered } = this.state;
    const { carousel_slots = [], index } = carouselData;
    const indexToUse = typeof carouselIndex !== 'undefined' ? carouselIndex : index;
    const showDefaultStyles = variant === 'default';
    const inCarouselPipExperiment =
      !isCloseup &&
      new SingleTreatmentExp(requestContext, 'web_carousel_pip_improvements').enabled();
    requestContext.experiments.flush();

    const CLOSEUP_ELLIPSIS_SIZE = (showDefaultStyles && isCloseup) || !isCloseup ? 8 : 16;
    const ELLIPSIS_SIZE = inCarouselPipExperiment ? 10 : CLOSEUP_ELLIPSIS_SIZE;
    const ELLIPSIS_SPACING = (isCloseup && showDefaultStyles) || inCarouselPipExperiment ? 2 : 1;
    const ellipsisLegoBackground = closeupImageHovered ? 'rgba(0, 0, 0, 0)' : 'rgba(0, 0, 0, 0.7)';
    const ELLIPSIS_LEGO_STYLE = {
      __style: {
        backgroundColor: isHovered ? 'rgba(0, 0, 0, 0.05)' : ellipsisLegoBackground,
      },
    };

    const selectedBgColor = showDefaultStyles ? 'white' : '#555';
    const bgColor = showDefaultStyles ? 'rgba(255, 255, 255, 0.5)' : 'lightGray';

    const ellipsis = [];
    for (let i = 0; i < carousel_slots.length; i += 1) {
      const isSelected = i === indexToUse;
      ellipsis.push(
        <Box key={pinId + i} marginRight={i !== carousel_slots.length - 1 ? ELLIPSIS_SPACING : 0}>
          <Touchable
            fullWidth={false}
            shape="circle"
            onTouch={({ event }) => {
              if (!isEditMode && typeof changeCarouselSlotIndex !== 'undefined') {
                event.preventDefault();
                event.stopPropagation();
                changeCarouselSlotIndex(String(pinId), i);
              }
              // special case for closeup bc we can't rely on store as source of truth
              if (typeof handleCarouselSwipe !== 'undefined') {
                handleCarouselSwipe(i);
              }

              logSwipe(
                pin,
                pinId,
                indexToUse,
                i,
                carouselData,
                viewParameter,
                viewType,
                componentType,
                contextLogData,
                isEditMode,
              );
            }}
          >
            <Box
              data-test-id="ellipsis-size"
              height={ELLIPSIS_SIZE}
              width={ELLIPSIS_SIZE}
              dangerouslySetInlineStyle={{
                __style: {
                  backgroundColor: isSelected ? selectedBgColor : bgColor,
                },
              }}
              shape="circle"
            />
          </Touchable>
        </Box>,
      );
    }

    return (
      <Box
        data-test-id="carousel-ellipsis"
        display="flex"
        {...(showDefaultStyles && isCloseup && !inCarouselPipExperiment
          ? {
              shape: 'pill',
              height: 44,
              justifyContent: 'center',
              alignItems: 'center',
              paddingX: 4,
              dangerouslySetInlineStyle: ELLIPSIS_LEGO_STYLE,
            }
          : {
              marginTop: 1,
              marginLeft: isEditMode ? 2 : -2,
              marginBottom: isEditMode ? 1 : 0,
              justifyContent: inCarouselPipExperiment ? 'center' : 'start',
              alignItems: inCarouselPipExperiment ? 'center' : 'stretch',
            })}
        onMouseEnter={showDefaultStyles ? this.handleMouseEnter : NOOP}
        onMouseLeave={showDefaultStyles ? this.handleMouseLeave : NOOP}
      >
        {ellipsis}
      </Box>
    );
  }
}

const mapStateToProps = (
  { pins }: ReduxState,
  { carouselData: carouselDataProp, pinId }: OwnProps,
): ReduxStateProps => {
  const pin = pins[pinId];
  return {
    pin,
    carouselData: carouselDataProp || getCarouselData(pin),
  };
};

const mapDispatchToProps = (dispatch: Dispatch<any>): ReduxDispatchProps => ({
  changeCarouselSlotIndex: (pinId, newIndex) => dispatch(changeCarouselSlotIndex(pinId, newIndex)),
});

export default compose(
  connect<ConnectedProps, OwnProps, ReduxStateProps, ReduxDispatchProps, ReduxState, _>(
    mapStateToProps,
    mapDispatchToProps,
  ),
  withRequestContext,
)(CarouselEllipsis);
